import { useState, useMemo } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Modal, Button, Combobox, NumberInput, useToast } from "@a2z/ui";
import { useCreateCheckoutLinkMutation } from "@/modules/products/services/useCreateCheckoutLinkMutation";
import type { ProductVariantView } from "@/lib/api/types";

type Props = {
  storeSlug: string;
  productId: string;
  variants: ProductVariantView[];
  /**
   * Visual variant for the trigger button.
   * - `default`: dark primary pill (used in list/detail surfaces).
   * - `onHero`: white solid pill with send icon, tuned for the dark navy
   *   hero slot on the edit-product page.
   */
  variant?: "default" | "onHero";
  /** Override the trigger button styling (e.g. a full-width detail-view row). */
  triggerClassName?: string;
  /** Raise the sheet above a full-screen overlay (e.g. the mobile product
   *  detail view, z-[70]) so it isn't hidden behind it. */
  elevated?: boolean;
};

export function ProductCheckoutLinkSheet({
  storeSlug,
  productId,
  variants,
  variant = "default",
  triggerClassName: triggerClassNameProp,
  elevated,
}: Props) {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    variants.length > 0 ? variants[0].id : null,
  );
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const { mutateAsync: createLink, isPending: isCreating } =
    useCreateCheckoutLinkMutation();

  // Build variant display names from selections
  const variantDisplayNames = useMemo(() => {
    const map = new Map<string, string>();
    variants.forEach((variant) => {
      const nameArParts = variant.selections.map((sel) => sel.optionValueAr).filter(Boolean);
      const nameEnParts = variant.selections.map((sel) => sel.optionValueEn).filter(Boolean);
      const nameAr = nameArParts.length > 0 ? nameArParts.join(" / ") : variant.id;
      const nameEn = nameEnParts.length > 0 ? nameEnParts.join(" / ") : variant.id;
      map.set(variant.id, `${nameAr}${nameEn && nameAr !== nameEn ? ` (${nameEn})` : ""}`);
    });
    return map;
  }, [variants]);

  const handleCopyLink = async () => {
    setIsProcessing(true);
    try {
      const response = await createLink({
        storeSlug,
        productId,
        productVariantId: selectedVariantId ?? undefined,
        quantity,
      });

      // Copy URL to clipboard
      await navigator.clipboard.writeText(response.url);

      // Show success toast
      toast.success(t(keys.products.linkCopiedToast));
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to create checkout link:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenSheet = () => {
    setSelectedVariantId(variants.length > 0 ? variants[0].id : null);
    setQuantity(1);
    setIsOpen(true);
  };

  const triggerClassName =
    variant === "onHero"
      ? "inline-flex h-9 items-center gap-1.5 rounded-lg bg-white px-3 text-meta font-semibold text-[color:var(--color-hero-surface)] transition hover:bg-white/90"
      : "inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--primary-500)] px-3 py-2 text-sm font-medium text-white transition hover:bg-[color:var(--primary-600)]";

  return (
    <>
      <button
        type="button"
        onClick={handleOpenSheet}
        className={triggerClassNameProp ?? triggerClassName}
        title={t(keys.products.sendToBuyerButton)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
        {t(keys.products.sendToBuyerButton)}
      </button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        elevated={elevated}
        title={t(keys.products.quickOrderLinkTitle)}
        closeLabel={t(keys.common.close)}
        hideFooter
      >
          <p className="mb-4 text-sm text-[color:var(--text-muted)]">
            {t(keys.products.checkoutLinkSheetHelper)}
          </p>

          {/* Variant selector */}
          {variants.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-[color:var(--text-secondary)] mb-2">
                {t(keys.products.checkoutLinkSheetVariantLabel)}
              </label>
              <Combobox
                options={variants.map((variant) => ({
                  value: variant.id,
                  label: variantDisplayNames.get(variant.id) || variant.id,
                }))}
                value={selectedVariantId}
                onChange={(value) => setSelectedVariantId(value)}
              />
            </div>
          )}

          {/* Quantity stepper */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[color:var(--text-secondary)] mb-2">
              {t(keys.products.checkoutLinkSheetQuantityLabel)}
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-primary)] transition disabled:opacity-50"
                aria-label={t(keys.products.checkoutLinkStepperDecrement)}
              >
                −
              </button>
              <NumberInput
                name="quantity"
                value={quantity}
                onChange={(e) => {
                  const val = Math.min(10, Math.max(1, parseInt(e.target.value) || 1));
                  setQuantity(val);
                }}
                min={1}
                max={10}
                className="h-11 w-16 rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-center text-sm text-[color:var(--text-primary)] focus:outline-none focus:border-[color:var(--primary-500)]"
              />
              <button
                type="button"
                onClick={() => setQuantity(Math.min(10, quantity + 1))}
                disabled={quantity >= 10}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-primary)] transition disabled:opacity-50"
                aria-label={t(keys.products.checkoutLinkStepperIncrement)}
              >
                +
              </button>
            </div>
          </div>

          {/* Copy link button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleCopyLink}
            disabled={isCreating || isProcessing}
          >
            {t(keys.products.checkoutLinkCopyButton)}
          </Button>
      </Modal>
    </>
  );
}
